import { insertMarkdownWithTypingReveal } from "./neoAiMarkdownInsert";
import { revealMarkdownAppend, revealMarkdownReplace } from "./neoAiMarkdownReplaceReveal";

/**
 * Same truncation suffix as backend `claude_service._truncate_document`
 * so anchor validation matches the string sent to `/ai/ask`.
 */
export const NEO_AI_MAX_DOCUMENT_MARKDOWN_CHARS = 120_000;

export function truncateMarkdownForNeoAi(markdown) {
  const md = typeof markdown === "string" ? markdown : "";
  if (md.length <= NEO_AI_MAX_DOCUMENT_MARKDOWN_CHARS) return md;
  return (
    md.slice(0, NEO_AI_MAX_DOCUMENT_MARKDOWN_CHARS) +
    "\n\n<!-- Trecho omitido: documento truncado para limite do NeoAi -->\n"
  );
}

/**
 * Apply structured NeoAi `edit` to the TipTap editor.
 *
 * @param {import("@tiptap/core").Editor} editor
 * @param {{
 *   edit: { edit_mode: string; anchor_start?: string | null; anchor_end?: string | null; markdown: string };
 *   documentMarkdownSent: string;
 *   cursorPos: number;
 *   signal?: AbortSignal;
 * }} params
 * @returns {Promise<{ ok: true } | { ok: false; reason: string }>}
 */
export async function applyNeoAiEdit(editor, params) {
  const { edit, documentMarkdownSent, cursorPos, signal } = params;
  if (!editor || !edit || typeof edit !== "object") {
    return { ok: false, reason: "missing_editor_or_edit" };
  }

  const mode = edit.edit_mode;
  const markdown = typeof edit.markdown === "string" ? edit.markdown : "";

  if (!markdown.trim()) {
    return { ok: false, reason: "empty_markdown" };
  }

  if (mode === "full_document") {
    const currentMd =
      editor && typeof editor.getMarkdown === "function"
        ? editor.getMarkdown()
        : documentMarkdownSent || "";
    await revealMarkdownReplace(editor, {
      prefix: "",
      middle: currentMd,
      suffix: "",
      incoming: markdown,
      signal,
      maxEraseFrames: 56,
      maxTypeFrames: 72,
    });
    return { ok: true };
  }

  if (mode === "append") {
    const base = (documentMarkdownSent || "").replace(/\s*$/, "");
    await revealMarkdownAppend(editor, base, markdown.trim(), { signal });
    return { ok: true };
  }

  if (mode === "replace_between_anchors") {
    const doc = documentMarkdownSent ?? "";
    const a0 = edit.anchor_start;
    const a1 = edit.anchor_end;
    if (typeof a0 !== "string" || typeof a1 !== "string" || !a0 || !a1) {
      return { ok: false, reason: "missing_anchors" };
    }
    const i = doc.indexOf(a0);
    if (i < 0) return { ok: false, reason: "anchor_start_not_found" };
    const j = doc.indexOf(a1, i + a0.length);
    if (j < 0 || j <= i) return { ok: false, reason: "anchor_end_not_found" };
    const prefix = doc.slice(0, i);
    const middle = doc.slice(i, j);
    const suffix = doc.slice(j);
    await revealMarkdownReplace(editor, {
      prefix,
      middle,
      suffix,
      incoming: markdown,
      signal,
      maxEraseFrames: 64,
      maxTypeFrames: 88,
    });
    return { ok: true };
  }

  if (mode === "insert_at_cursor") {
    const pos =
      typeof cursorPos === "number" && Number.isFinite(cursorPos)
        ? cursorPos
        : editor.state.selection.from;
    await insertMarkdownWithTypingReveal(editor, markdown, pos, { signal });
    return { ok: true };
  }

  return { ok: false, reason: "unknown_edit_mode" };
}
